import { Button, message, Popconfirm, Result } from "antd";
import debounce from "lodash/debounce";
import { FC, useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "../assets/icons/icon";
import { renderErrorMessage } from "../components/messages/errorMessage";
import DataTable from "../components/table/dataTable";
import { CustomColumnsType, RenderFunc } from "../components/table/dataTableTypes";
import UserStore from "../contexts/userStore";
import _t from "../lang/translate";
import PageTitle from "../layout/pageTitle";
import { getPath } from "../routes/appRoutes";
import { customersUrl, deleteCustomer } from "../services/customerService";
import { useEnums } from "../services/enumService";
import { pageSizeKey, searchKey, typeKey } from "../services/urlQueryService";
import { useApi } from "../services/useApi";
import { PaginationedData } from "../types/apiTypes";
import { IdType, LocationStateType, ScreenProps } from "../types/appTypes";
import { CustomerListItemType } from "../types/customerTypes";
import CustomersExport from "./forms/customer/customersExport";

const Customers: FC<ScreenProps> = ({ title }) => {
  const location = useLocation<LocationStateType>();
  const { hasPermission } = UserStore.useContainer();
  const [refreshId, setRefreshId] = useState<string | undefined>();
  const [{ data, isLoading, isError }, setUrl, setData] = useApi<PaginationedData<CustomerListItemType>>("", {
    data: [],
  });
  const [enums] = useEnums();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (typeof refreshId === "string") {
      query.set("refreshId", refreshId);
    }
    query.set(pageSizeKey, localStorage.getItem(pageSizeKey) || "20");
    const url = customersUrl(query.toString());
    query.has(searchKey) ? delayedSetUrl(url) : setUrl(url);
  }, [setUrl, location.search, refreshId]);

  // If doing as it recommends, table search stops working
  const delayedSetUrl = useCallback(
    debounce((url: string) => {
      setUrl(url);
    }, 200), [setUrl]
  );

  const handleDelete = async (id: IdType) => {
    const originalData = { ...data };
    try {
      setData((state) => ({
        ...state,
        data: originalData.data.filter((item) => item.id !== id),
      }));
      await deleteCustomer(id);
      message.success(_t("deleted"));
    } catch (ex) {
      setData(originalData);
      renderErrorMessage(ex, _t("msg.not_deleted"))
    }
  };

  const columns: CustomColumnsType<CustomerListItemType> = [
    {
      title: "#",
      align: "center",
      key: "id",
      sorter: true,
      dataIndex: "id",
      width: 110,
      render: (text, record) =>
        hasPermission("screen.customer") ? (
          <Link to={getPath("customer", record.id)}>
            <strong>{text}</strong>
          </Link>
        ) : (
          text
        ),
      fixed: "left",
    },
    {
      title: _t("prospectId"),
      key: "prospectId",
      dataIndex: "prospectId",
      sorter: true,
      hidden: true,
    },
    {
      title: _t("cvr_or_cpr"),
      key: "cvr",
      dataIndex: "cvr",
      sorter: true,
      width: 120,
      render: (text, record) => text || record.cpr,
    },
    {
      title: _t("name"),
      key: "name",
      dataIndex: "name",
      ellipsis: true,
      sorter: true,
    },
    {
      title: _t("address"),
      key: "address",
      dataIndex: "address",
      sorter: true,
      hidden: true,
      ellipsis: true,
    },
    {
      title: _t("zip", "city", " & "),
      key: "zip",
      dataIndex: "zip",
      sorter: true,
      hidden: true,
      render: (text, record) => `${text ? text : ""} ${record.city ? record.city : ""}`,
    },
    {
      title: _t("phone"),
      key: "phone",
      sorter: true,
      dataIndex: "phone",
      width: 160,
    },
    {
      title: _t("email"),
      key: "email",
      dataIndex: "email",
      sorter: true,
    },
    {
      title: _t("contact_person"),
      key: "contactPerson",
      sorter: true,
      dataIndex: "contactPerson",
      hidden: true,
    },
    {
      title: _t("type"),
      key: "type",
      dataIndex: "type",
      width: 100,
    },
  ];

  const tableActions: RenderFunc<CustomerListItemType> = (_, record) => {
    return hasPermission("customer.delete") ? (
      <Popconfirm
        placement="topLeft"
        onConfirm={() => handleDelete(record.id)}
        icon={<Icon fill="red" name="information-circle-outline" />}
        title={
          <div>
            {_t("msg.confirm_delete")}&nbsp;
            <strong>
              {record.id} : {record.name}
            </strong>
          </div>
        }
      >
        <Button className="muted delete-btn" type="text" shape="circle" icon={<Icon name="trash-outline" />} />
      </Popconfirm>
    ) : null;
  };

  const handleRefresh = () => setRefreshId(new Date().getSeconds().toString());

  return (
    <>
      <PageTitle
        fluid
        title={title}
        extra={
          <>
            {hasPermission("customer.export") && <CustomersExport />}
            {hasPermission("customer.create") && (
              <>
                <span key="newCustomer">{_t("new_customer")}: </span>
                {enums?.customer?.type?.map((item) => (
                  <Button value={item.id} type="primary" key={item.id}>
                    <Link to={`${getPath("customer", "new")}?${typeKey}=${item.id}`}>{item.title}</Link>
                  </Button>
                ))}
              </>
            )}{" "}
          </>
        }
      />
      {isError ? (
        <Result status="error" title={_t("msg.unknown_error")} />
      ) : (
        <DataTable<CustomerListItemType>
          loading={isLoading}
          onRefresh={handleRefresh}
          renderActions={tableActions}
          columnStorageKey={"CUSTOMER"}
          columns={columns}
          dataSource={data.data}
          meta={data.meta}
        />
      )}
    </>
  );
};

export default Customers;
